import React from "react";
import PropTypes from "prop-types";

import { ROUND_SIZES, THEMES, THEME_LABELS } from "./options";

// Three genders rather than two, so three colours. Blue and purple carry over
// from French. Neuter is amber rather than the obvious teal, which sat too
// close to the green used for a correct answer - and the gender colour lands on
// the word while right/wrong lands on the buttons, so they must not rhyme.
// All three clear 4.5:1 on white and 3:1 on the dark background.
export const DEFAULT_COLORS = { f: "#a032b8", m: "#1565c0", n: "#8d6e00" };

export const GENDER_NAMES = { f: "feminine", m: "masculine", n: "neuter" };

export default function Settings({
  audioAvailable,
  autoAdvance,
  roundSize,
  autoplayAudio,
  background,
  soundEffects,
  colors,
  colorCoding,
  systemDark,
  theme,
  loopAudio,
  onSetColor,
  onSetRoundSize,
  onToggleAutoAdvance,
  onToggleAutoplayAudio,
  onToggleBackground,
  onToggleSoundEffects,
  onToggleColorCoding,
  onSetTheme,
  onToggleLoopAudio,
  onToggleShuffle,
  shuffle,
}) {
  return (
    <div className="gna-settings">
      <label className="gna-setting" htmlFor="gna-round-size">
        Round length
        <select
          className="gna-setting-select"
          id="gna-round-size"
          onChange={(e) => onSetRoundSize(Number(e.target.value))}
          value={roundSize}
        >
          {ROUND_SIZES.map((size) => (
            <option key={size} value={size}>
              {size} words
            </option>
          ))}
        </select>
      </label>

      <label className="gna-setting" htmlFor="gna-shuffle">
        <input checked={shuffle} id="gna-shuffle" onChange={onToggleShuffle} type="checkbox" />
        Shuffle
        <span className="gna-setting-note">
          {shuffle ? "random order" : "most common first"} · restarts the round
        </span>
      </label>

      <label className="gna-setting" htmlFor="gna-auto-advance">
        <input
          checked={autoAdvance}
          id="gna-auto-advance"
          onChange={onToggleAutoAdvance}
          type="checkbox"
        />
        Move on automatically
        <span className="gna-setting-note">when you're right</span>
      </label>

      <label className="gna-setting" htmlFor="gna-sound-effects">
        <input
          checked={soundEffects}
          id="gna-sound-effects"
          onChange={onToggleSoundEffects}
          type="checkbox"
        />
        Sound effects
        <span className="gna-setting-note">on a correct answer</span>
      </label>

      {audioAvailable && (
        <>
          <label className="gna-setting" htmlFor="gna-autoplay">
            <input
              checked={autoplayAudio}
              id="gna-autoplay"
              onChange={onToggleAutoplayAudio}
              type="checkbox"
            />
            Play audio after answering
          </label>

          <label className="gna-setting" htmlFor="gna-loop-audio">
            <input
              checked={loopAudio}
              id="gna-loop-audio"
              onChange={onToggleLoopAudio}
              type="checkbox"
            />
            Loop the audio
          </label>
        </>
      )}

      <div className="gna-setting">
        <label htmlFor="gna-theme">Theme</label>
        <select
          className="gna-setting-select"
          id="gna-theme"
          onChange={(e) => onSetTheme(e.target.value)}
          value={theme}
        >
          {THEMES.map((option) => (
            <option key={option} value={option}>
              {THEME_LABELS[option]}
            </option>
          ))}
        </select>
        {theme === "auto" && (
          <span className="gna-setting-note">
            following your system ({systemDark ? "dark" : "light"})
          </span>
        )}
      </div>

      <label className="gna-setting" htmlFor="gna-background">
        <input
          checked={background}
          id="gna-background"
          onChange={onToggleBackground}
          type="checkbox"
        />
        Background image
      </label>

      <label className="gna-setting" htmlFor="gna-color-coding">
        <input
          checked={colorCoding}
          id="gna-color-coding"
          onChange={onToggleColorCoding}
          type="checkbox"
        />
        Color code by gender
      </label>

      {colorCoding && (
        <div className="gna-colors">
          {["m", "f", "n"].map((gender) => (
            <label className="gna-color" htmlFor={`gna-color-${gender}`} key={gender}>
              <input
                id={`gna-color-${gender}`}
                onChange={(e) => onSetColor(gender, e.target.value)}
                type="color"
                value={colors[gender]}
              />
              <span style={{ color: colors[gender] }}>{GENDER_NAMES[gender]}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

Settings.propTypes = {
  audioAvailable: PropTypes.bool.isRequired,
  autoAdvance: PropTypes.bool.isRequired,
  roundSize: PropTypes.number.isRequired,
  autoplayAudio: PropTypes.bool.isRequired,
  background: PropTypes.bool.isRequired,
  soundEffects: PropTypes.bool.isRequired,
  colorCoding: PropTypes.bool.isRequired,
  colors: PropTypes.objectOf(PropTypes.string).isRequired,
  systemDark: PropTypes.bool.isRequired,
  theme: PropTypes.oneOf(THEMES).isRequired,
  loopAudio: PropTypes.bool.isRequired,
  onSetColor: PropTypes.func.isRequired,
  onSetRoundSize: PropTypes.func.isRequired,
  onToggleAutoAdvance: PropTypes.func.isRequired,
  onToggleAutoplayAudio: PropTypes.func.isRequired,
  onToggleBackground: PropTypes.func.isRequired,
  onToggleSoundEffects: PropTypes.func.isRequired,
  onToggleColorCoding: PropTypes.func.isRequired,
  onSetTheme: PropTypes.func.isRequired,
  onToggleLoopAudio: PropTypes.func.isRequired,
  onToggleShuffle: PropTypes.func.isRequired,
  shuffle: PropTypes.bool.isRequired,
};
